import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import clsx from "clsx";
import dayjs from "dayjs";

import loadEmpty from "assets/images/loads-post-2.png";
import Toggle from "components/Toggle";
import { routes } from "constants/routes";
import { showErrorMessage } from "helpers/toastMessage";
import { useUserFeature } from "hooks/useUserFeature";
import { QUERY_LOAD_QUESTION } from "../../Detail/query";
import LoadPostedItem from "../../modules/LoadPostedItem/LoadPostedItem";
import { QUERY_HAULPAY_LOAD_LIST, QUERY_MY_LOAD_LIST } from "./query";
import styles from "./style.module.scss";
import { RefLoadingSpinner } from "components/LoadingSpinner";

type LoadPostedListProps = {
  is_haulpay?: boolean;
};

export default function LoadPostedList({ is_haulpay }: Readonly<LoadPostedListProps>) {
  const navigate = useNavigate();
  const { isDispatcher } = useUserFeature();

  const [isRefresh, setIsRefresh] = useState(true);
  const [isCurrentLoad, setIsCurrentLoad] = useState(true);
  const [myLoadsList, setMyLoadsList] = useState<any[]>([]);

  const ContainerRef = useRef<HTMLDivElement>(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [endCursor, setEndCursor] = useState();

  const { data, loading, error, refetch } = useQuery(
    is_haulpay ? QUERY_HAULPAY_LOAD_LIST : QUERY_MY_LOAD_LIST,
    {
      variables: {
        orderBy: "-time_posted",
        first: 20
      },
      fetchPolicy: "cache-and-network"
    }
  );

  const { refetch: questionRefetch } = useQuery(QUERY_LOAD_QUESTION, {
    fetchPolicy: "cache-and-network",
    skip: true
  });

  const questionRefresh = async (id: string) => {
    await questionRefetch({
      loadId: id
    });
  };

  useEffect(() => {
    if (data) {
      if (isRefresh) {
        setMyLoadsList(data.myLoads.data);
      } else {
        setMyLoadsList((prev) => [...prev, ...data.myLoads.data]);
      }
      setHasNextPage(data.myLoads.pageInfo.hasNextPage);
      setEndCursor(data.myLoads.pageInfo.endCursor);
    }
  }, [data]);

  useEffect(() => {
    if (!loading && error) {
      showErrorMessage(error.message);
    }
  }, [loading]);

  const myLoadList = useMemo(
    () =>
      myLoadsList
        ? myLoadsList
            .map((load: any, index) => ({
              ...load,
              index,
              timeDelivery: {
                timeDelivery: dayjs(load.timeDelivery).format("DD MMM YYYY"),
                deliveryDay: dayjs(load.timeDelivery).format("DD MMM"),
                deliveryTime: dayjs(load.timeDelivery).format("hh:mm A")
              },
              timeStart: {
                timeStart: dayjs(load.timeStart).format("DD MMM YYYY"),
                startDay: dayjs(load.timeStart).format("DD MMM"),
                startTime: dayjs(load.timeStart).format("hh:mm A")
              },
              timeEnd: {
                timeEnd: dayjs(load.timeEnd).format("DD MMM YYYY"),
                endDay: dayjs(load.timeEnd).format("DD MMM"),
                endTime: dayjs(load.timeEnd).format("hh:mm A")
              }
            }))
            .filter((item) => (isCurrentLoad ? !item.isDeleted : item.isDeleted))
        : [],
    [myLoadsList, isCurrentLoad]
  );

  function editLoad(id: string) {
    navigate(`${routes.LOADS_POST}/edit/${id}`);
  }

  function goDetail(id: string) {
    navigate(`${routes.LOADS_POST}/${id}`, {
      state: {
        my_load: true
      }
    });
  }

  const onScroll = async () => {
    if (ContainerRef.current) {
      const { scrollTop, offsetHeight, scrollHeight } = ContainerRef.current;
      if (!!scrollTop && scrollTop + offsetHeight + 1 >= scrollHeight && hasNextPage) {
        setIsRefresh(false);
        await refetch({
          orderBy: "-time_posted",
          first: 20,
          after: endCursor
        });
      }
    }
  };

  useEffect(() => {
    refetch();
  }, []);

  return (
    <>
      <div className={styles.postLoad}>
        <Toggle
          prefix="Only current loads"
          checked={isCurrentLoad}
          className={clsx("pt-1", isDispatcher && "mt-5")}
          onChange={(event) => setIsCurrentLoad(event.target.checked)}
        />
      </div>

      <div className={styles.loadPostedListContainer} ref={ContainerRef} onScroll={onScroll}>
        {loading && (
          <div className="py-3">
            <RefLoadingSpinner isLoadingMore={loading} />
          </div>
        )}
        {myLoadList.length ? (
          myLoadList.map((item) => (
            <LoadPostedItem
              key={item.id}
              data={item}
              onEdit={editLoad}
              onClickDetails={goDetail}
              refresh={refetch}
              setIsRefresh={setIsRefresh}
              questionRefresh={() => questionRefresh(item.id)}
            />
          ))
        ) : (
          <div className={styles.loadPostEmptyList}>
            <img src={loadEmpty} alt="Empty load list" />
            <h3 className={styles.loadPostEmptyTitle}>No Loads Yet</h3>
            <p className="font-medium text-center">
              Try posting some. <br /> Old posts are in History
            </p>
          </div>
        )}
      </div>
    </>
  );
}
